import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";

const STEPS = [
  { n: 1, code: "1", label: "Strategic Foundations", phase: "PEOPLE" },
  { n: 2, code: "2", label: "Stakeholder & Channels", phase: "PEOPLE" },
  { n: 3, code: "3", label: "Strategy & Messaging", phase: "PEOPLE" },
  { n: 4, code: "4", label: "Threat & Risk", phase: "PEOPLE" },
];
const LAST_STEP = STEPS.length;

const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent";
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent";

const empty = {
  businessGoals: "", objectives: ["", "", ""], internalStakeholders: "", externalStakeholders: "",
  channels: "", strategy: "", keyMessages: ["", "", ""], risks: [{ risk: "", control: "" }],
};

export default function CommunicationStrategy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [coPilotOpen, setCoPilotOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        const p = res.data.payload || {};
        setData({
          ...empty,
          ...p,
          objectives: [...(p.objectives || []), "", "", ""].slice(0, 3),
          keyMessages: [...(p.keyMessages || []), "", "", ""].slice(0, 3),
          risks: (p.risks && p.risks.length) ? p.risks : [{ risk: "", control: "" }],
        });
      } catch {
        toast.error("Document not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setArr = (key, i, value) => setData((d) => ({ ...d, [key]: d[key].map((v, j) => (j === i ? value : v)) }));
  const setRisk = (i, patch) => setData((d) => ({ ...d, risks: d.risks.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const addRisk = () => setData((d) => ({ ...d, risks: [...d.risks, { risk: "", control: "" }] }));
  const removeRisk = (i) => setData((d) => ({ ...d, risks: d.risks.filter((_, j) => j !== i) }));

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/documents/${id}`, { title: title || "Untitled Communication Strategy", payload: data, ...extra });
      return true;
    } catch {
      toast.error("Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, title, data]);

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST_STEP, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => {
    if (await persist({ status: "Completed" })) {
      toast.success("Communication strategy finalised");
      navigate(`/communication/${id}/view`);
    }
  };

  const applySuggestion = (text) => {
    const append = (existing) => (!existing || !existing.trim() ? text : `${existing.trimEnd()}\n- ${text}`);
    if (step === 1) set({ businessGoals: append(data.businessGoals) });
    else if (step === 2) set({ channels: append(data.channels) });
    else if (step === 3) set({ strategy: append(data.strategy) });
    else { setData((d) => ({ ...d, risks: [...d.risks, { risk: text, control: "" }] })); }
    toast.success("Suggestion added to this step");
  };

  if (loading)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft size={16} /> Workspace
        </button>
        <input data-testid="communication-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Communication strategy title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="communication-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`rounded-sm border px-3 py-2.5 text-left transition-all ${step === s.n ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold ${step === s.n ? "text-teal-700" : "text-slate-400"}`}>STEP {s.n}</span>
              <p className={`text-sm font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Strategic Foundations</h2>
              <div>
                <label className={labelCls}>Business Goal(s)</label>
                <textarea data-testid="field-businessGoals" rows={4} className={taCls} value={data.businessGoals} onChange={(e) => set({ businessGoals: e.target.value })} placeholder="Define the overarching business goal or core objective this communication serves." />
              </div>
              <div>
                <label className={labelCls}>Communication Objectives</label>
                <div className="space-y-2">
                  {data.objectives.map((o, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className="w-7 h-10 shrink-0 rounded-sm bg-teal-50 border border-teal-200 text-teal-700 font-mono text-xs font-bold flex items-center justify-center">O{i + 1}</span>
                      <input data-testid={`field-objective-${i}`} value={o} onChange={(e) => setArr("objectives", i, e.target.value)} placeholder={`Objective ${i + 1}`} className={inputCls + " flex-1"} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Stakeholder & Channel Mapping</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Internal Stakeholders</label>
                  <textarea data-testid="field-internalStakeholders" rows={5} className={taCls} value={data.internalStakeholders} onChange={(e) => set({ internalStakeholders: e.target.value })} placeholder="Critical internal audiences, alignment requirements, engagement protocols." />
                </div>
                <div>
                  <label className={labelCls}>External Stakeholders</label>
                  <textarea data-testid="field-externalStakeholders" rows={5} className={taCls} value={data.externalStakeholders} onChange={(e) => set({ externalStakeholders: e.target.value })} placeholder="External clients, regulators, contractors or public interfaces." />
                </div>
              </div>
              <div>
                <label className={labelCls}>Channels</label>
                <textarea data-testid="field-channels" rows={3} className={taCls} value={data.channels} onChange={(e) => set({ channels: e.target.value })} placeholder="Map the exact delivery mechanisms — e.g. Town Halls, Toolboxes, Executive Briefs, Electronic Media." />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">Strategy & Core Messaging</h2>
              <div>
                <label className={labelCls}>Strategy & Methodology</label>
                <textarea data-testid="field-strategy" rows={5} className={taCls} value={data.strategy} onChange={(e) => set({ strategy: e.target.value })} placeholder="Tactical rollout logic, timing cadence and engagement approach." />
              </div>
              <div>
                <label className={labelCls}>Key Messages</label>
                <div className="space-y-2">
                  {data.keyMessages.map((m, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className="w-7 h-10 shrink-0 rounded-sm bg-teal-50 border border-teal-200 text-teal-700 font-mono text-xs font-bold flex items-center justify-center">M{i + 1}</span>
                      <input data-testid={`field-message-${i}`} value={m} onChange={(e) => setArr("keyMessages", i, e.target.value)} placeholder={`Message ${i + 1}`} className={inputCls + " flex-1"} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Threat & Risk Mitigation</h2>
                <button data-testid="add-risk-button" onClick={addRisk} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <Plus size={16} /> Add risk
                </button>
              </div>
              <p className="text-sm text-slate-500">List communication leaks, messaging friction points or misunderstandings — alongside a direct defensive control.</p>
              <div className="space-y-3">
                {data.risks.map((r, i) => (
                  <div key={i} data-testid={`risk-row-${i}`} className="border border-slate-200 rounded-sm p-3">
                    <div className="flex items-start gap-3">
                      <span className="mt-2 font-mono text-xs text-slate-400 w-5">{i + 1}.</span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                        <div>
                          <label className={labelCls}>Risk to Mitigate</label>
                          <textarea data-testid={`risk-${i}`} rows={2} className={taCls} value={r.risk} onChange={(e) => setRisk(i, { risk: e.target.value })} placeholder="Potential leak, friction or misunderstanding…" />
                        </div>
                        <div>
                          <label className={labelCls}>Defensive Control</label>
                          <textarea data-testid={`control-${i}`} rows={2} className={taCls} value={r.control} onChange={(e) => setRisk(i, { control: e.target.value })} placeholder="The control task that mitigates it…" />
                        </div>
                      </div>
                      <button data-testid={`remove-risk-${i}`} onClick={() => removeRisk(i)} className="mt-2 p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="communication-back" onClick={back} disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="communication-save-draft" onClick={saveDraft} disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft
            </button>
            {step < LAST_STEP ? (
              <button data-testid="communication-next" onClick={next}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                Next Step <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="communication-finalise" onClick={finalise} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                <CheckCircle size={18} weight="fill" /> Finalise Strategy
              </button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={coPilotOpen} onClick={() => setCoPilotOpen(true)} phase={STEPS[step - 1].phase} />
      <CoPilotPanel open={coPilotOpen} onClose={() => setCoPilotOpen(false)} docId={id} toolType="communication_strategy" step={STEPS[step - 1]} phase={STEPS[step - 1].phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
